import * as React from 'react';
import { LazyLoadTypes } from 'react-slick';

import { ImagePlaceholder } from '@notino/react-styleguide';

import ErrorBoundary from '@components/ErrorBoundary';
import Slider from '@components/Slider';
import { IImageProperties } from '@helpers/utils';
import { noop } from '@utils/noop';

import { youtubeIframe } from '../../../utils';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { ModiFaceOverlay } from '../ModiFaceOverlay';

import {
  ImageSliderWrapper,
  PlayerWrapper,
  ImgWrapper,
  StyledArrow,
} from './styled';

export interface IImageSliderProps {
  imagesAttrs: IImageProperties[];
  videos?: string[];
  hasModiface?: boolean;
  openModifaceModal?: () => void;
}

const getSliderSettings = (newPdDesign: boolean) => ({
  centerMode: true,
  infinite: false,
  cssEase: 'ease-in-out',
  centerPadding: '0px',
  slidesToShow: 1,
  arrows: true,
  prevArrow: <StyledArrow direction="left" newPdDesign={newPdDesign} />,
  nextArrow: <StyledArrow direction="right" newPdDesign={newPdDesign} />,
  lazyLoad: 'ondemand' as LazyLoadTypes,
});

export const ImageSlider: React.FC<IImageSliderProps> = React.memo(
  ({ imagesAttrs, videos, hasModiface, openModifaceModal }) => {
    const newPdDesign = useNewPdDesignEnabled();

    return (
      <ErrorBoundary>
        <ImageSliderWrapper newPdDesign={newPdDesign}>
          <Slider conf={getSliderSettings(newPdDesign)}>
            {imagesAttrs.map((imgProps, index) => {
              const showModifaceOverlay = hasModiface && index === 0;
              // Don't display placeholder for the first image in the slider.
              // An image replace placeholder when is loaded and after react is hydrated.
              // React hydration can take some time and we want first image to show immediately when loaded.
              const img = (
                <ImagePlaceholder
                  hidePlaceholder={index === 0}
                  image={imgProps}
                  onLoad={noop}
                />
              );
              // if modiface overlay on mobile, clone main image so customer can swipe to image a view it without overlay
              if (showModifaceOverlay) {
                return [
                  <ImgWrapper
                    key={`${imgProps.src}-modiface`}
                    newPdDesign={newPdDesign}
                  >
                    <>
                      {img}
                      <ModiFaceOverlay onClick={openModifaceModal} />
                    </>
                  </ImgWrapper>,
                  <ImgWrapper key={imgProps.src} newPdDesign={newPdDesign}>
                    {img}
                  </ImgWrapper>,
                ];
              }
              return (
                <ImgWrapper key={imgProps.src} newPdDesign={newPdDesign}>
                  {img}
                </ImgWrapper>
              );
            })}
            {videos.map((video) => (
              <PlayerWrapper
                newPdDesign={newPdDesign}
                key={video}
                dangerouslySetInnerHTML={{
                  __html: youtubeIframe(video),
                }}
              />
            ))}
          </Slider>
        </ImageSliderWrapper>
      </ErrorBoundary>
    );
  }
);
